import React, { useState, useEffect } from 'react';
import { ClipboardList, Plus, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import AnimatedModal from './AnimatedModal';

const AddToFavorites = ({ productId, productName, className = "", buttonText = null }) => {
  const [showModal, setShowModal] = useState(false);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState('');

  useEffect(() => {
    if (showModal) {
      fetchLists();
    }
  }, [showModal]);

  const fetchLists = async () => {
    try {
      const response = await apiService.get('/favorites/lists');
      setLists(response.data.lists);
    } catch (error) {
      console.error('Erreur lors du chargement des listes:', error);
      toast.error('Erreur lors du chargement des listes');
    }
  };

  const handleAddToList = async (listId) => {
    setLoading(true);
    try {
      await apiService.post(`/favorites/lists/${listId}/products`, {
        product_id: productId
      });
      toast.success('Produit ajouté à la liste');
      setShowModal(false);
    } catch (error) {
      console.error('Erreur ajout produit:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'ajout');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateListAndAdd = async (e) => {
    e.preventDefault();
    if (!newListName.trim()) return;

    setLoading(true);
    try {
      // Créer la liste
      const listResponse = await apiService.post('/favorites/lists', {
        name: newListName.trim(),
        description: ''
      });

      // Ajouter le produit à la nouvelle liste
      await apiService.post(`/favorites/lists/${listResponse.data.list.id}/products`, {
        product_id: productId
      });

      toast.success('Liste créée et produit ajouté');
      setShowModal(false);
      setShowCreateForm(false);
      setNewListName('');
    } catch (error) {
      console.error('Erreur création liste:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setShowModal(true);
    setShowCreateForm(false);
  };

  return (
    <>
      <button
        onClick={openModal}
        className={className || "flex items-center justify-center text-gray-600 hover:text-orange-500 transition-colors"}
        title="Ajouter à une liste"
      >
        {buttonText ? (
          <>
            <ClipboardList className="h-4 w-4 mr-2" />
            {buttonText}
          </>
        ) : (
          <ClipboardList className="h-4 w-4" />
        )}
      </button>

      <AnimatedModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)}
        size="md"
      >
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Ajouter à une liste
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Choisissez une liste pour <strong>{productName}</strong>
            </p>

            {!showCreateForm ? (
              <>
                {lists.length === 0 ? (
                  <div className="text-center py-4">
                    <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">Vous n'avez pas encore de liste</p>
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="btn-primary flex items-center mx-auto"
                    >
                      <Plus className="h-4 w-4 md:mr-2" />
                      <span className="hidden md:inline">Créer ma première liste</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {lists.map((list) => (
                      <button
                        key={list.id}
                        onClick={() => handleAddToList(list.id)}
                        disabled={loading}
                        className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        <div className="font-medium text-gray-900">{list.name}</div>
                        <div className="text-sm text-gray-500">
                          {list.product_count} produit{list.product_count !== 1 ? 's' : ''}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {lists.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="w-full btn-secondary flex items-center justify-center"
                    >
                      <Plus className="h-4 w-4 md:mr-2" />
                      <span className="hidden md:inline">Créer une nouvelle liste</span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <form onSubmit={handleCreateListAndAdd}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de la nouvelle liste *
                  </label>
                  <input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    className="input-field"
                    placeholder="Ex: Équipement photo"
                    required
                    autoFocus
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading || !newListName.trim()}
                  >
                    {loading ? 'Création...' : 'Créer et ajouter'}
                  </button>
                </div>
              </form>
            )}
        </div>
      </AnimatedModal>
    </>
  );
};

export default AddToFavorites;
